/**
 * The shape of the note field set, shared by resolving an inbox item as a note and adding
 * or editing one directly.
 *
 * Separate from the components so `NoteFields.tsx` exports components only — React Fast
 * Refresh cannot hot-swap a module that also exports values (D48).
 */

export type NoteFieldValues = {
  title: string;
  body: string;
  /** `""` | `"area:<id>"` | `"project:<id>"` — same encoding `DestinationPicker` uses. */
  destination: string;
};

export const EMPTY_NOTE_FIELDS: NoteFieldValues = { title: "", body: "", destination: "" };

/** Turn the picker's combined encoding back into the ids the API wants. */
export function destinationToIds(destination: string): {
  area_id?: string;
  project_id?: string;
} {
  if (destination.startsWith("project:")) return { project_id: destination.slice(8) };
  if (destination.startsWith("area:")) return { area_id: destination.slice(5) };
  return {};
}

/** The title the AI is handed for a note: what was typed, or — for a note that is only a body, which
 * the AI button accepts — the body's first line. */
export function noteAiTitle(values: NoteFieldValues): string {
  return values.title.trim() || values.body.trim().split("\n")[0].slice(0, 120);
}

/** A saved note as the form shows it. */
export function noteToFieldValues(note: {
  title: string;
  body: string | null;
  area_id: string | null;
  project_id: string | null;
}): NoteFieldValues {
  return {
    title: note.title,
    body: note.body ?? "",
    destination: note.project_id
      ? `project:${note.project_id}`
      : note.area_id
        ? `area:${note.area_id}`
        : "",
  };
}
